import {
  Button,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
  getRecipeColor,
  Grid,
  ControlledSelect,
} from '@ui-components/neo.tax';
import { ReactComponent as Parts } from '@ui-components/neo.tax/dist/icons/model-parts-2.svg';
import { useEffect } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { ProjectBusinessComponent, ProjectBusinessPurpose, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

import { BUSINESS_COMPONENT_OPTIONS, BUSINESS_PURPOSE_OPTIONS } from './constants';
import { useRdProjectBusinessComponentIds } from './hooks';
import { StyledProjectEligibilityModal } from './rd-project-business-component-modal.styled';

const SUPPORTED_BUSINESS_COMPONENTS = [
  ProjectBusinessComponent.ComputerSoftware,
  ProjectBusinessComponent.Product,
  ProjectBusinessComponent.Process,
];

const getValidBusinessComponent = (
  component: ProjectBusinessComponent | null | undefined,
): ProjectBusinessComponent => {
  if (component && SUPPORTED_BUSINESS_COMPONENTS.includes(component)) {
    return component;
  }
  return ProjectBusinessComponent.ComputerSoftware;
};

type RdProjectBusinessComponentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rdProject: RdProject;
};

type RdProjectBusinessComponentForm = {
  businessComponent: ProjectBusinessComponent | null;
  businessPurpose: ProjectBusinessPurpose | null;
};

export const RdProjectBusinessComponentModal = ({
  isOpen,
  onClose,
  rdProject,
}: RdProjectBusinessComponentModalProps): React.ReactElement => {
  const { businessComponentId, businessPurposeId } = useRdProjectBusinessComponentIds();
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    setValue,
  } = useForm<RdProjectBusinessComponentForm>({
    mode: 'onChange',
    defaultValues: {
      businessComponent: getValidBusinessComponent(rdProject.businessComponent),
      businessPurpose: rdProject.businessPurpose ?? null,
    },
  });

  const selectedBusinessComponent = useWatch({
    control,
    name: 'businessComponent',
  });

  const isBusinessPurposeVisible = selectedBusinessComponent === ProjectBusinessComponent.ComputerSoftware;

  // Reset business purpose when component changes to non-software
  useEffect(() => {
    if (!isBusinessPurposeVisible) {
      setValue('businessPurpose', null, { shouldDirty: true });
    }
  }, [selectedBusinessComponent, isBusinessPurposeVisible, setValue]);

  const isFormValid = isValid && isDirty;

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<RdProjectBusinessComponentForm> = async (data) => {
    await updateRdProject(data);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        businessComponent: getValidBusinessComponent(rdProject.businessComponent),
        businessPurpose: rdProject.businessPurpose ?? null,
      });
    }
  }, [isOpen, rdProject.businessComponent, rdProject.businessPurpose, reset]);

  return (
    <StyledProjectEligibilityModal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={8} onSubmit={handleSubmit(onSubmit)} paddingX={4}>
        <ModalHeader
          icon={<Parts width={24} color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').text} />}
          iconBgColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
          title={
            <Typography fontSize="base" fontWeight="semibold">
              Project{' '}
              <Typography
                fontSize="inherit"
                fontWeight="inherit"
                color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}
              >
                {rdProject.name}
              </Typography>{' '}
              Business Component
            </Typography>
          }
          copy="Certain activities are explicitly disqualified from the R&D credit"
        />
        <Grid cols={2} gap={4}>
          <ControlledSelect
            label="What is the type of business component?"
            name="businessComponent"
            options={BUSINESS_COMPONENT_OPTIONS}
            id={businessComponentId}
            control={control}
            rules={{ required: true }}
          />
          {isBusinessPurposeVisible && (
            <ControlledSelect
              label="What is the business purpose of this component?"
              name="businessPurpose"
              options={BUSINESS_PURPOSE_OPTIONS}
              id={businessPurposeId}
              control={control}
              placeholder="Select business purpose"
              rules={{ required: true }}
            />
          )}
        </Grid>
        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit" disabled={!isFormValid}>
            Save
          </Button>
        </ModalActions>
      </Stack>
    </StyledProjectEligibilityModal>
  );
};
